"use client";
import React from 'react';
import './CampaignSnapshot.css';
import styles from './CampaignSnapshot.module.css';

const ProgressBar = ({ progress, label, color = '#0043ce' }) => {
  return (
    <div className="progress-container">
      <div className="progress-track">
        <div
          className="progress-bar"
          style={{
            width: `${progress}%`,
            backgroundColor: color
          }}
        />
      </div>
      {label && <div className="progress-label">{label}</div>}
    </div>
  );
};

const MetricCard = ({ icon, value, label, change }) => {
  return (
    <article className="metric-card">
      <img src={icon} alt="" className="metric-icon" />
      <div className="metric-content">
        <h3 className="metric-value">{value}</h3>
        <p className="metric-label">{label}</p>
        <p className="metric-change">{change}</p>
      </div>
    </article>
  );
};

const DetailCard = ({
  icon,
  title,
  value,
  description,
  progress,
  progressLabel,
  additionalStats
}) => {
  return (
    <article className="detail-card">
      <img src={icon} alt="" className="detail-icon" />
      <div className="detail-content">
        <p className="detail-title">{title}</p>
        <h3 className="detail-value">{value}</h3>
        <p className="detail-description">{description}</p>

        {progress !== undefined && (
          <ProgressBar
            progress={progress}
            label={progressLabel}
          />
        )}

        {additionalStats && (
          <div className="stats-container">
            {additionalStats.map((stat, index) => (
              <div key={index} className="stat-box">
                <p className="stat-label">{stat.label}</p>
                <p className="stat-value">{stat.value}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </article>
  );
};

const CampaignSnapshot = ({ budget, targetViews, currentViews, latestViews, percentChange, latestLikes, latestComments, likesCommentsPercentChange, engagementRate, audience }) => {
  // 格式化budget为$1,000,000
  function formatBudget(val) {
    if (val === undefined || val === null) return '-';
    return `$${Number(val).toLocaleString()}`;
  }
  function formatViews(val) {
    if (val === undefined || val === null) return '-';
    const num = Number(val);
    if (num >= 1e9) {
      return (num / 1e9).toFixed(1) + ' B';
    } else if (num >= 1e6) {
      return (num / 1e6).toFixed(1) + ' M';
    } else if (num >= 1e3) {
      return (num / 1e3).toFixed(1) + ' K';
    } else {
      return num.toFixed(1);
    }
  }

  const isAllEmpty =
    (budget === null || budget === undefined) &&
    (targetViews === null || targetViews === undefined) &&
    (currentViews === null || currentViews === undefined) &&
    (latestViews === null || latestViews === undefined) &&
    (percentChange === null || percentChange === undefined) &&
    (latestLikes === null || latestLikes === undefined) &&
    (latestComments === null || latestComments === undefined) &&
    (likesCommentsPercentChange === null || likesCommentsPercentChange === undefined) &&
    (engagementRate === null || engagementRate === undefined);

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;700&family=Lato:wght@400;700&display=swap"
        rel="stylesheet"
      />
      {isAllEmpty ? (
        <section className={styles.campaignContainer}>
          <div style={{padding: 60, textAlign: 'center', color: '#888', fontSize: 20, width: '100%'}}>No snapshot data available for this campaign.</div>
        </section>
      ) : (
        <section className={styles.campaignContainer}>
          <h1 className={styles.campaignTitle}>Campaign Snapshot</h1>

          <div className={styles.metricsGrid}>
            <MetricCard
              icon="https://cdn.builder.io/api/v1/image/assets/TEMP/1b8ec06cb55da2eb85cd533724847da704140364?placeholderIfAbsent=true"
              value={latestViews !== null && latestViews !== undefined ? formatViews(latestViews) : '-'}
              label="Total Views"
              change={percentChange !== null && percentChange !== undefined ? (percentChange > 0 ? '+' : '') + Math.round(percentChange) + '%' : '-'}
            />
            <MetricCard
              icon="https://cdn.builder.io/api/v1/image/assets/TEMP/816559c11ddab17025693a37232f154118fcfd99?placeholderIfAbsent=true"
              value={latestLikes !== null && latestComments !== null && latestLikes !== undefined && latestComments !== undefined ? formatViews(latestLikes + latestComments) : '-'}
              label="Total Engagement"
              change={likesCommentsPercentChange !== null && likesCommentsPercentChange !== undefined ? (likesCommentsPercentChange > 0 ? '+' : '') + Math.round(likesCommentsPercentChange) + '%' : '-'}
            />
            {/* <MetricCard
              icon="https://cdn.builder.io/api/v1/image/assets/TEMP/6e87de78f29b6c3b3ca2cc5a54204cc7f202c53f?placeholderIfAbsent=true"
              value="5.80%"
              label="Est. Conversion Rate"
            /> */}
          </div>

          <div className={styles.detailCardsGrid}>
            <DetailCard
              icon="https://cdn.builder.io/api/v1/image/assets/TEMP/f4817dfa80691a20e3673f39307135a8283e783e?placeholderIfAbsent=true"
              title="Budget"
              value={formatBudget(budget)}
              description="Budget Utilization"
              progress={100}
              progressLabel={`${100}%`}
            />

            {(() => {
              let progress = 0;
              if (currentViews !== undefined && currentViews !== null && targetViews) {
                progress = Math.min((currentViews / targetViews) * 100, 100);
              }
              const progressLabel = `${progress.toFixed(1)}% to complete`;
              return (
                <DetailCard
                  icon="https://cdn.builder.io/api/v1/image/assets/TEMP/76329d8fefa7feccc794d3339b35554ae3fed8c5?placeholderIfAbsent=true"
                  title="Campaign Goal"
                  value="Demand Generation"
                  description="Boost your brand visibility and drive product engagement"
                  progress={progress}
                  progressLabel={progressLabel}
                  additionalStats={[
                    { label: 'Target', value: formatViews(targetViews) },
                    { label: 'Current', value: currentViews !== undefined && currentViews !== null ? formatViews(currentViews) : '-' }
                  ]}
                />
              );
            })()}

            <DetailCard
              icon="https://cdn.builder.io/api/v1/image/assets/TEMP/310a5a2713c6427e8c1bc48d68fe4cedc3b000c1?placeholderIfAbsent=true"
              title="Target Region"
              value="Geographical Reach"
              description="Campaign targets audiences"
              progress={50}
              progressLabel="Coverage"
              additionalStats={[
                { label: 'Audience', value: formatViews(audience) }
              ]}
            />
          </div>
        </section>
      )}
    </>
  );
};

export default CampaignSnapshot;